import { useState } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { AvatarUpload } from '@/components/ui/AvatarUpload'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

const SECTIONS = ['Profile', 'Workspace', 'Notifications', 'Appearance']
const ACCENT_COLORS = ['#e63946', '#a78bfa', '#60a5fa', '#34d399', '#fb923c']

export function SettingsModal({ open, onClose, initialSection = 'Profile' }) {
  const { currentUser, workspace, theme: storeTheme, accentColor: storeAccentColor, notifPrefs, setNotifPrefs, setAccentColor: storeSetAccentColor } = useAppStore()
  const setThemeInStore = useAppStore((s) => s.setTheme)
  const updateProfile = useAppStore((s) => s.updateProfile)
  const changePassword = useAppStore((s) => s.changePassword)
  const [section, setSection] = useState(initialSection)
  const [name, setName] = useState(currentUser?.name || '')
  const [email, setEmail] = useState(currentUser?.email || '')
  const [password, setPassword] = useState('')
  const [wsName, setWsName] = useState(workspace?.name || '')
  const [theme, setTheme] = useState(storeTheme || 'dark')
  const [accentColor, setAccentColor] = useState(storeAccentColor || '#e63946')
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState(null)

  function handleThemeChange(t) {
    setTheme(t)
    setThemeInStore?.(t)
  }

  function handleAccentChange(color) {
    setAccentColor(color)
    storeSetAccentColor(color)
  }

  function toggleNotification(key) {
    setNotifPrefs({ [key]: !notifPrefs[key] })
  }

  async function handleSave() {
    setSaveError(null)
    try {
      if (section === 'Profile') {
        await updateProfile({ name, email })
        if (password.trim()) {
          const { error } = await changePassword(password)
          if (error) { setSaveError(error.message); return }
          setPassword('')
        }
      } else if (section === 'Workspace') {
        await updateProfile({ workspaceName: wsName })
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 1500)
    } catch (err) {
      setSaveError(err.message || 'Something went wrong')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Settings" size="md">
      <div className="flex gap-5 -mt-1">
        {/* Nav */}
        <div className="w-36 flex-shrink-0 space-y-0.5">
          {SECTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setSection(s)}
              className={cn(
                'w-full text-left px-3 h-8 rounded-lg text-sm transition-colors active:scale-[0.97]',
                section === s ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text hover:bg-surface-raised'
              )}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-4">
          {section === 'Profile' && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <AvatarUpload size="w-12 h-12" shape="rounded-full" textSize="text-base" />
                <div>
                  <p className="text-sm font-medium text-text">{name || 'Your Name'}</p>
                  <p className="text-xs text-text-muted">{email || 'your@email.com'}</p>
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Full name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" />
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Change password…" />
              </div>
            </>
          )}

          {section === 'Workspace' && (
            <>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Workspace name</label>
                <Input value={wsName} onChange={(e) => setWsName(e.target.value)} placeholder="Workspace name" />
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Workspace URL</label>
                <div className="flex items-center gap-2 h-8 bg-surface-raised border border-border rounded-lg px-3 text-sm text-text-muted">
                  prjct.app/
                  <span className="text-text">{wsName.toLowerCase().replace(/\s+/g, '-') || 'workspace'}</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Members</label>
                <div className="bg-surface-raised border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center text-accent text-xs font-bold">{name[0]?.toUpperCase() || 'U'}</div>
                    <div>
                      <p className="text-xs font-medium text-text">{name || 'You'}</p>
                      <p className="text-[10px] text-text-muted">Admin</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {section === 'Notifications' && (
            <div className="space-y-3">
              {[
                ['assignments', 'Task assignments', 'When a task is assigned to you'],
                ['mentions', '@Mentions', 'When someone mentions you in a comment'],
                ['completions', 'Task completions', 'When a task you own is completed'],
                ['reminders', 'Due date reminders', '24 hours before a task is due'],
                ['comments', 'New comments', 'When someone comments on your tasks'],
                ['invites', 'Team invitations', 'When you invite someone and they accept'],
              ].map(([key, label, desc]) => {
                const on = notifPrefs[key]
                return (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-text">{label}</p>
                      <p className="text-xs text-text-muted">{desc}</p>
                    </div>
                    <button
                      onClick={() => toggleNotification(key)}
                      aria-pressed={on}
                      aria-label={`Toggle ${label}`}
                      className={cn(
                        'w-11 h-6 rounded-full flex-shrink-0 relative transition-colors duration-200',
                        on ? 'bg-accent' : 'bg-surface-raised border border-border'
                      )}
                    >
                      <span
                        className="absolute top-0.5 left-0 w-5 h-5 rounded-full bg-white shadow-sm"
                        style={{
                          transform: on ? 'translateX(22px)' : 'translateX(2px)',
                          transition: 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1)',
                        }}
                      />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {section === 'Appearance' && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-text-muted mb-2 block">Theme</label>
                <div className="flex gap-2">
                  {['dark', 'light', 'system'].map((t) => (
                    <button
                      key={t}
                      onClick={() => handleThemeChange(t)}
                      className={cn(
                        'flex-1 h-9 rounded-lg text-sm capitalize transition-all active:scale-[0.97]',
                        theme === t ? 'bg-accent/10 text-accent border border-accent/30' : 'bg-surface-raised text-text-muted border border-border hover:text-text'
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-2 block">Accent color</label>
                <div className="flex gap-2">
                  {ACCENT_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => handleAccentChange(c)}
                      aria-label={`Set accent color to ${c}`}
                      className="w-7 h-7 rounded-full active:scale-[0.94] transition-transform"
                      style={{
                        background: c,
                        outline: accentColor === c ? `2px solid ${c}` : '2px solid transparent',
                        outlineOffset: 2,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {saveError && <p className="text-xs text-red-400 -mb-1">{saveError}</p>}
          <div className="flex gap-2 pt-2 border-t border-border mt-4">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>
              {saved ? '✓ Saved' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
